import { runMediaSave, type MediaSaveLifecycle } from './mediaSaveLifecycle';
// WebSignaturePad.tsx
// --------------------------------------------
// Componente de assinatura **somente Web** usando `react-signature-canvas`.
// - Mantém a mesma API de callbacks (`onOK`, `onClear`, `onClose`) usada no
//   ChecklistSignatureField.
// - Resolve DPI alto (retina) escalonando o canvas.
// - Garante traço contínuo (deixa o lib cuidar do path; nós apenas lidamos
//   com o "begin" para saber quando não está vazio).
// - Valida assinatura vazia antes de emitir o PNG.
// - Em modo retrato gera PNG rotacionado 90° para ficar em pé no relatório.
// - Usa apenas dependências já presentes no projeto (`react-native`,
//   `react-signature-canvas`, `expo/vector-icons`).
//-------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Text,
    Dimensions,
    Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../config/theme';

interface WebSignaturePadProps extends MediaSaveLifecycle {
    /** Callback quando o usuário salva. PNG é entregue já rotacionado se necessário */
    onOK: (dataUrl: string) => void;
    /** Opcional – aviso quando o usuário clica em Limpar */
    onClear?: () => void;
    /** Opcional – fecha o modal */
    onClose?: () => void;
    /**
     * Força modo retrato (útil quando o componente é usado dentro de um modal
     * de retrato fixo). Se não for passado, a orientação segue o viewport.
     */
    isPortrait?: boolean;
}

/**
 * WebSignaturePad – componente React/TSX completo
 */
export default function WebSignaturePad({
    onOK,
    onSaveStart, onSaveCancel, onSaveError, onDrawingEvent,
    onClear,
    onClose,
    isPortrait: forcedPortrait,
}: WebSignaturePadProps) {
    //-------------------------------------------
    // Refs e estados
    //-------------------------------------------
    const drawingStarted = useRef(false);
    const noteDrawing = () => {
        if (drawingStarted.current) return;
        drawingStarted.current = true;
        onDrawingEvent?.('drawing_started');
    };
    const sigRef = useRef<SignatureCanvas>(null);

    const [canvasSize, setCanvasSize] = useState({ width: 300, height: 200 });
    const [isEmpty, setIsEmpty] = useState(true);
    const [isPortrait, setIsPortrait] = useState<boolean>(
        forcedPortrait ?? true,
    );

    //-------------------------------------------
    // Atualiza layout quando viewport muda
    //-------------------------------------------
    const updateLayout = useCallback(() => {
        const { width, height } = Dimensions.get('window');
        const portrait = height >= width;

        // Mantém orientação forçada (se houver)
        setIsPortrait(forcedPortrait ?? portrait);

        // Calcula dimensões máximas respeitando margens
        let w: number, h: number;
        if (forcedPortrait ?? portrait) {
            // Retrato – mais alto que largo
            w = Math.min(width *0.7);
            h = Math.min(height * 0.8);
        } else {
            // Paisagem – tradicional 5:3
            w = Math.min(width - 80, 800);
            h = Math.min(w * 0.6, 300);
        }
        setCanvasSize({ width: w, height: h });
    }, [forcedPortrait]);

    useEffect(() => {
        updateLayout();
        const sub = Dimensions.addEventListener('change', updateLayout);
        return () => sub.remove();
    }, [updateLayout]);

    //-------------------------------------------
    // Handlers de assinatura
    //-------------------------------------------
    const handleBegin = () => { noteDrawing(); setIsEmpty(false); };

    const handleClear = () => {
    onDrawingEvent?.('drawing_cleared');
        sigRef.current?.clear();
        setIsEmpty(true);
        onClear?.();
    };

    /**
     * Converte o canvas em PNG, faz validações e rotação se for retrato.
     */
    const handleSave = () => {
        if (!sigRef.current) throw new Error('Canvas indisponível ao salvar.');

        // Retornado pela lib: o canvas DOM nativo
        const originalCanvas = sigRef.current.getCanvas();
        const { width, height } = originalCanvas;

        // 1) Retina – se necessário reescala internamente para manter nitidez
        const ratio = Math.max(1, window.devicePixelRatio || 1);
        if (ratio > 1 && width < canvasSize.width * ratio) {
            const tmp = document.createElement('canvas');
            tmp.width = canvasSize.width * ratio;
            tmp.height = canvasSize.height * ratio;
            const tctx = tmp.getContext('2d')!;
            tctx.scale(ratio, ratio);
            tctx.drawImage(originalCanvas, 0, 0);

            // Copia de volta para o canvas original
            originalCanvas.width = tmp.width;
            originalCanvas.height = tmp.height;
            originalCanvas.getContext('2d')!.drawImage(tmp, 0, 0);
        }

        // 2) Verifica se a assinatura não está vazia (pixel alpha > 0)
        const octx = originalCanvas.getContext('2d')!;
        const pixels = octx.getImageData(0, 0, originalCanvas.width, originalCanvas.height).data;
        const hasInk = (() => {
            for (let i = 3; i < pixels.length; i += 4) {
                if (pixels[i] > 0) return true;
            }
            return false;
        })();

        if (!hasInk) {
            onDrawingEvent?.('drawing_empty'); onSaveCancel?.();
            alert('Assinatura vazia. Desenhe antes de salvar.');
            return;
        }

        // 3) Se retrato, gira 90° antihorário (canvas novo)
        let finalCanvas: HTMLCanvasElement;
        if (isPortrait) {
            finalCanvas = document.createElement('canvas');
            finalCanvas.width = originalCanvas.height; // invertido
            finalCanvas.height = originalCanvas.width;
            const fctx = finalCanvas.getContext('2d')!;

            fctx.save();
            fctx.translate(0, finalCanvas.height);
            fctx.rotate(-Math.PI / 2);
            fctx.drawImage(originalCanvas, 0, 0);
            fctx.restore();
        } else {
            finalCanvas = originalCanvas;
        }

        // 4) Gera PNG
        const dataUrl = finalCanvas.toDataURL('image/png');
        onOK(dataUrl);
    };

    //-------------------------------------------
    // JSX
    //-------------------------------------------
    return (
        <View style={styles.container}>
            {/* -------- Cabeçalho -------- */}
            <View style={styles.header}>
                {onClose && (
                    <TouchableOpacity
                        accessibilityLabel="Fechar assinatura"
                        accessibilityRole="button"
                        onPress={onClose}
                        style={styles.iconButton}
                    >
                        <Ionicons name="close" size={24} color={theme.colors.text.primary} />
                    </TouchableOpacity>
                )}
                <Text style={styles.title}>Assinatura</Text>
            </View>

            {/* -------- Conteúdo principal -------- */}
            {isPortrait ? (
                <View style={styles.centerWrapper}>
                    <View style={styles.mainContent}>
                        {/* Barra lateral com botões */}
                        <View style={styles.sidebar}>
                            <TouchableOpacity
                                accessibilityLabel="Limpar assinatura"
                                accessibilityRole="button"
                                onPress={handleClear}
                                style={styles.sideButton}
                                disabled={isEmpty}
                            >
                                <Text style={styles.sideButtonText} numberOfLines={1} ellipsizeMode="tail">Limpar</Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                                accessibilityLabel="Salvar assinatura"
                                accessibilityRole="button"
                                onPress={() => runMediaSave(handleSave, { onSaveStart, onSaveError })}
                                style={[styles.sideButton, styles.saveSideButton, isEmpty && styles.disabledButton]}
                                disabled={isEmpty}
                            >
                                <Text style={[styles.sideButtonText, styles.saveSideButtonText]} numberOfLines={1} ellipsizeMode="tail">Salvar</Text>
                            </TouchableOpacity>
                        </View>
                        {/* Canvas de assinatura */}
                        <View style={[styles.canvasWrapper, { width: canvasSize.width, height: canvasSize.height }]}> 
                            <SignatureCanvas
                                ref={sigRef}
                                onBegin={handleBegin}
                                penColor={theme.colors.text.primary}
                                backgroundColor={theme.colors.white + '00'}
                                clearOnResize={false}
                                canvasProps={{
                                    width: canvasSize.width,
                                    height: canvasSize.height,
                                    className: 'sigCanvas',
                                    style: {
                                        border: `1px solid ${theme.colors.border.default}`,
                                        borderRadius: theme.borderRadius.xs,
                                        width: `${canvasSize.width}px`,
                                        height: `${canvasSize.height}px`,
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        touchAction: 'none',
                                    },
                                }}
                            />
                        </View>
                    </View>
                </View>
            ) : (
                <>
                    {/* -------- Canvas -------- */}
                    <View style={styles.canvasWrapper}>
                        <SignatureCanvas
                            ref={sigRef}
                            onBegin={handleBegin}
                            penColor={theme.colors.text.primary}
                            backgroundColor={theme.colors.white + '00'}
                            clearOnResize={false}
                            canvasProps={{
                                width: canvasSize.width,
                                height: canvasSize.height,
                                className: 'sigCanvas',
                                style: {
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: theme.borderRadius.xs,
                                    width: `${canvasSize.width}px`,
                                    height: `${canvasSize.height}px`,
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    touchAction: 'none',
                                },
                            }}
                        />
                    </View>
                    {/* -------- Rodapé -------- */}
                    <View style={styles.footer}>
                        <TouchableOpacity
                            accessibilityLabel="Limpar assinatura"
                            accessibilityRole="button"
                            onPress={handleClear}
                            style={styles.button}
                            disabled={isEmpty}
                        >
                            <Text style={styles.buttonText}>Limpar</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            accessibilityLabel="Salvar assinatura"
                            accessibilityRole="button"
                            onPress={() => runMediaSave(handleSave, { onSaveStart, onSaveError })}
                            style={[styles.button, styles.saveButton, isEmpty && styles.disabledButton]}
                            disabled={isEmpty}
                        >
                            <Text style={[styles.buttonText, styles.saveButtonText]}>Salvar</Text>
                        </TouchableOpacity>
                    </View>
                </>
            )}
        </View>
    );
}

//-------------------------------------------
// Estilos – usa StyleSheet para consistência com theme
//-------------------------------------------
const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.background.primary,
        justifyContent: 'space-between',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    iconButton: {
        marginRight: theme.spacing.sm,
    },
    title: {
        fontSize: theme.fontSizes.lg,
        fontWeight: theme.fontWeights.semibold as any,
        color: theme.colors.text.primary,
    },
    // --- Portrait layout ---
    centerWrapper: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    mainContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    sidebar: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing.md,
    },
    sideButton: {
        borderWidth: 1,
        borderColor: theme.colors.border.dark,
        borderRadius: theme.borderRadius.sm,
        width: 60,
        height: 140,
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: theme.spacing.xs,
        backgroundColor: theme.colors.background.secondary,
        ...theme.shadows.sm,
        padding: 0,
    },
    sideButtonText: {
        fontSize: theme.fontSizes.md,
        color: theme.colors.text.primary,
        fontWeight: theme.fontWeights.medium as any,
        transform: [{ rotate: '90deg' }],
        width: 110,
        textAlign: 'center',
    },
    saveSideButton: {
        backgroundColor: theme.colors.primary.dark,
        borderColor: theme.colors.primary.dark,
    },
    saveSideButtonText: {
        color: theme.colors.text.inverse,
    },
    disabledButton: {
        opacity: 0.5,
    },
    // --- Landscape layout (footer) ---
    canvasWrapper: {
        alignSelf: 'center',
        borderRadius: theme.borderRadius.sm,
        overflow: 'hidden',
        ...theme.shadows.sm,
        borderWidth: 1,
        borderColor: theme.colors.border.light, // Borda vermelha para visualização
        alignItems: 'center',
        justifyContent: 'center',
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: theme.spacing.md,
    },
    button: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        marginHorizontal: theme.spacing.xs,
        borderRadius: theme.borderRadius.sm,
        borderWidth: 1,
        borderColor: theme.colors.border.dark,
        alignItems: 'center',
        backgroundColor: theme.colors.background.secondary,
        ...theme.shadows.sm,
    },
    saveButton: {
        backgroundColor: theme.colors.primary.dark,
        borderColor: theme.colors.primary.dark,
    },
    buttonText: {
        fontSize: theme.fontSizes.sm,
        color: theme.colors.text.primary,
        fontWeight: theme.fontWeights.medium as any,
    },
    saveButtonText: {
        color: theme.colors.text.inverse,
    },
});
